import { useState } from "react";
import { Download, FileText, ClipboardCheck, ShieldAlert, PlayCircle, BookOpen, FileBox, Loader2, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { RESOURCES, IMAGES } from "../lib/data";
import { PageHero, SectionHead, CTABand } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import api, { formatApiErrorDetail } from "../lib/api";
import { getVisitorId, getAttribution, trackEvent } from "../lib/track";

const TYPE_ICON = {
  "Guide": BookOpen,
  "Checklist": ClipboardCheck,
  "Data Sheet": FileText,
  "Safety Guide": ShieldAlert,
  "Training Video": PlayCircle,
  "Literature": FileBox,
};

import { Seo } from "../components/site/Seo";

export default function Resources() {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const openRequest = (r) => {
    setSelected(r);
    setForm({ name: "", email: "", company: "" });
    setErrors({});
    setSent(false);
    trackEvent("cta_click", { label: `Resource requested: ${r.title}`, product: "Resource Center" });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your full name.";
    if (!form.email.trim()) e.email = "Please enter your work email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/resource/request", {
        name: form.name, email: form.email, company: form.company || "",
        resource_title: selected.title, resource_type: selected.type, resource_file: selected.file || "",
        site_url: window.location.origin,
        visitor_id: getVisitorId(), attribution: getAttribution(),
      });
      setSent(true);
      toast.success("Your resource is ready — check your inbox too.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="resources-page">
      <Seo title="Resource Center" description="Safety guides, checklists, spill response references, training, and product documentation for field crews." />
      <PageHero
        kicker="Resource Center"
        title="Field-ready knowledge, on demand."
        subtitle="Safety guides, checklists, spill response references, training, and product documentation — everything your crews need to stay prepared and compliant."
        image={IMAGES.pipeline}
      />

      <section className="bg-background">
        <div className="container-x py-24">
          <SectionHead kicker="Downloads & Training" title="Guides, checklists & documentation" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {RESOURCES.map((r, i) => {
              const Icon = TYPE_ICON[r.type] || FileText;
              return (
                <Reveal key={r.title} delay={(i % 3) * 0.06}>
                  <div className="group relative border border-border bg-card p-7 h-full flex flex-col overflow-hidden hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]">
                    <span aria-hidden="true" className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-flame to-transparent origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
                    <Icon aria-hidden="true" className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 text-border/30 group-hover:text-flame/10 transition-colors duration-500" strokeWidth={1} />
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="h-11 w-11 bg-flame/10 border border-flame/25 text-flame flex items-center justify-center group-hover:bg-flame group-hover:text-white group-hover:scale-110 transition-[transform,background-color,color] duration-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="kicker text-muted-foreground">{r.type}</span>
                    </div>
                    <h3 className="relative z-10 mt-5 font-display font-bold text-lg text-ink leading-snug group-hover:text-flame transition-colors">{r.title}</h3>
                    <p className="relative z-10 mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{r.desc}</p>
                    <button
                      data-testid={`resource-download-${i}`}
                      onClick={() => openRequest(r)}
                      className="relative z-10 mt-6 inline-flex items-center gap-2 text-ink font-semibold group-hover:text-flame transition-colors"
                    >
                      <Download className="h-4 w-4" /> Get resource
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent data-testid="resource-request-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-xl text-ink flex items-center gap-2">
              <Mail className="h-5 w-5 text-flame" /> Get "{selected?.title}"
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {sent
                ? "You're all set — check your inbox."
                : "Tell us where to send it and our team will email your copy shortly."}
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div data-testid="resource-request-sent" className="py-2">
              <div className="flex items-start gap-3 text-ink">
                <div className="h-10 w-10 bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><Check className="h-5 w-5" /></div>
                <div>
                  <p className="font-semibold">You're all set</p>
                  <p className="text-sm text-muted-foreground mt-1">We've emailed a copy to <strong>{form.email}</strong>. Download it right now below.</p>
                </div>
              </div>
              {selected?.file && (
                <a data-testid="resource-download-now" href={`/${selected.file}`} download target="_blank" rel="noopener noreferrer"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-5 py-3 font-semibold hover:bg-flame/90 transition-colors">
                  <Download className="h-4 w-4" /> Download "{selected.title}"
                </a>
              )}
            </div>
          ) : (
            <form onSubmit={submit} data-testid="resource-request-form" className="space-y-4" noValidate>
              <Field label="Full name *" testid="resource-name" value={form.name} error={errors.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: "" }); }} />
              <Field label="Work email *" testid="resource-email" type="email" value={form.email} error={errors.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: "" }); }} />
              <Field label="Company" testid="resource-company" value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <button data-testid="resource-submit" disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-5 py-3 font-semibold hover:bg-flame/90 disabled:opacity-50 transition-colors">
                {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <><Download className="h-4 w-4" /> Get the resource</>}
              </button>
              <p className="text-xs text-muted-foreground text-center">We'll only use your details to send this resource and follow up.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <CTABand />
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange}
        aria-invalid={!!error}
        className={`w-full border bg-background px-3.5 py-2.5 text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 ${error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-border focus:border-flame focus:ring-flame"}`} />
      {error && <p data-testid={`${testid}-error`} className="mt-1.5 text-xs text-red-600 font-medium">{error}</p>}
    </div>
  );
}
